import { type FormEvent, useCallback, useEffect, useState } from 'react'

import {
  useVendingMachineExtServicesSettings,
  useVendingMachineExtServicesSettingsMutate,
} from 'app/dataProviders/VendingDataProvider/machines/externalServices'
import { useVendingSettings } from 'app/dataProviders/VendingDataProvider'
import { VendingMachineExternalServiceSettings } from 'app/dataTypes/vending/machine/externalServices'
import { UNUSED_EXTERNAL_SERVICE_ID } from './predefined-ids'

interface Options {
  machineId: number
}

type AvailablePermissions = {
  allowExternalService: boolean
}
type CurrentPermissions = {
  enableExternalServices: boolean
}

type SettingsState = {
  availablePermissions: AvailablePermissions
  currentPermissions: CurrentPermissions
  machineExtService: VendingMachineExternalServiceSettings[]
  selectedExternalService: string[]
  loading: boolean
  pending: boolean
  modified: boolean
}

const defaultAvailablePermissions: AvailablePermissions = {
  allowExternalService: false,
}
const defaultCurrentPermissions: CurrentPermissions = {
  enableExternalServices: false,
}

type SettingsActions = {
  request: () => Promise<void>
  modify: (values: string[]) => void
  submit: (e?: FormEvent<HTMLFormElement>) => Promise<boolean>
}

export function useExternalServicesSettings({
  machineId,
}: Options): [SettingsState, SettingsActions] {
  const { allowVendingExternalService: allowExternalService, loading: loadingAllowSettings } =
    useVendingSettings()

  const [availablePermissions, setAvailablePermissions] = useState<AvailablePermissions>(
    defaultAvailablePermissions,
  )
  const [currentPermissions, setCurrentPermissions] =
    useState<CurrentPermissions>(defaultCurrentPermissions)
  const [selectedExternalService, setSelectedExternalService] = useState<string[]>([
    UNUSED_EXTERNAL_SERVICE_ID,
  ])

  const [modified, setModified] = useState(false)
  const [modifiedSettings, setModifiedSettings] = useState(false)

  const {
    data: dataExtService,
    loading: loadingExtServiceSettings,
    requestRef,
  } = useVendingMachineExtServicesSettings({
    params: { machineId },
  })

  const { updateService, updateSettings, pending } = useVendingMachineExtServicesSettingsMutate({
    machineId,
  })

  const handleModify = useCallback(
    (values: string[]) => {
      const nextSelectedExternalService = values.length ? values : [UNUSED_EXTERNAL_SERVICE_ID]
      const enableExternalServices =
        allowExternalService && nextSelectedExternalService[0] !== UNUSED_EXTERNAL_SERVICE_ID
      setSelectedExternalService(nextSelectedExternalService)
      setCurrentPermissions((prev) => ({
        ...prev,
        enableExternalServices,
      }))
      const currentServiceId = dataExtService.services.find(({ active }) => active)?.serviceId
      setModified(nextSelectedExternalService[0] !== currentServiceId)
      setModifiedSettings(enableExternalServices !== dataExtService.enableExternalServices)
    },
    [allowExternalService, dataExtService],
  )

  const handleUnModify = useCallback(() => {
    setModified(false)
    setModifiedSettings(false)
  }, [])

  const handleSubmit = useCallback(
    async (e?: FormEvent<HTMLFormElement>) => {
      if (e && e.target !== e.currentTarget) {
        return false
      }
      e?.preventDefault()
      const selectedExternalServiceForm = selectedExternalService[0]

      let saved = false
      if (modifiedSettings) {
        saved = await updateSettings(currentPermissions) || saved
      }
      if (
        currentPermissions.enableExternalServices
        && modified
        && selectedExternalServiceForm !== UNUSED_EXTERNAL_SERVICE_ID
      ) {
        saved = await updateService({
          serviceId: parseInt(selectedExternalServiceForm, 10),
          active: true,
        }) || saved
      }

      if (saved) {
        await requestRef.current()
      }

      handleUnModify()
      return true
    },
    [
      handleUnModify,
      updateSettings,
      updateService,
      modified,
      modifiedSettings,
      currentPermissions,
      selectedExternalService,
      requestRef,
    ],
  )

  const handleRequest = useCallback(async () => {
    requestRef.current()
    handleUnModify()
  }, [requestRef, handleUnModify])

  useEffect(() => {
    setAvailablePermissions((prev) => ({ ...prev, allowExternalService }))
  }, [allowExternalService])

  useEffect(() => {
    const { enableExternalServices } = dataExtService
    const currentServiceId = dataExtService.services.find(({ active }) => active)?.serviceId
    const externalService =
      enableExternalServices && currentServiceId
        ? String(currentServiceId)
        : UNUSED_EXTERNAL_SERVICE_ID

    setCurrentPermissions({ enableExternalServices })
    setSelectedExternalService([externalService])
  }, [dataExtService])

  return [
    {
      availablePermissions,
      currentPermissions,
      machineExtService: dataExtService.services,
      selectedExternalService,
      pending,
      loading: loadingAllowSettings || loadingExtServiceSettings,
      modified: modified || modifiedSettings,
    },
    {
      request: handleRequest,
      modify: handleModify,
      submit: handleSubmit,
    },
  ]
}
